/**
 * 🌌 Constellation Merchant Banner — global mount.
 *
 * Mounted once in App.js. Polls /api/constellation/state every 60s while
 * the user is logged in. When the user's per-user spawn is active, renders
 * a sticky purple→gold banner across every page with a live MM:SS countdown
 * and a "Visit Shop →" CTA that routes to /constellation.
 *
 * Per-user only — never global. Other users see nothing.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60 * 1000;   // poll for new spawns every 60s

export default function ConstellationBanner() {
  const { isAuthed } = useAuth();
  const location = useLocation();
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const expiresAtRef = useRef(null);

  // Poll the merchant state — triggers the 20% spawn roll server-side.
  useEffect(() => {
    if (!isAuthed) { setState(null); return; }
    let cancelled = false;
    const fetchState = async () => {
      try {
        const r = await axios.get(`${API}/constellation/state`);
        if (!cancelled) {
          setState(r.data);
          if (r.data?.active && r.data?.expires_at) {
            expiresAtRef.current = new Date(r.data.expires_at).getTime();
          } else {
            expiresAtRef.current = null;
          }
        }
      } catch {
        /* swallow — banner is best-effort */
      }
    };
    fetchState();
    const id = setInterval(fetchState, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAuthed]);

  // Live tick for countdown
  useEffect(() => {
    if (!state?.active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state?.active]);

  if (!isAuthed || !state?.active) return null;
  // Don't shout the banner at the user when they're already in the shop
  if (location.pathname?.startsWith("/constellation")) return null;

  const expiresAt = expiresAtRef.current;
  const secondsRemaining = expiresAt
    ? Math.max(0, Math.floor((expiresAt - now) / 1000))
    : (state.seconds_remaining || 0);
  if (secondsRemaining <= 0) return null;
  const mm = String(Math.floor(secondsRemaining / 60)).padStart(2, "0");
  const ss = String(secondsRemaining % 60).padStart(2, "0");

  return (
    <div
      data-testid="constellation-banner"
      className="fixed left-0 right-0 z-30 w-full pointer-events-none"
      style={{ bottom: "calc(64px + env(safe-area-inset-bottom))" }}
      role="alert"
      aria-label="Constellation Merchant has arrived"
    >
      <div className="bg-gradient-to-r from-purple-700 via-fuchsia-600 to-amber-500 shadow-[0_-4px_24px_rgba(168,85,247,0.45)] border-y border-amber-300/40 relative overflow-hidden pointer-events-auto">
        {/* Star sparkle background pattern */}
        <div className="absolute inset-0 opacity-20 pointer-events-none select-none flex items-center justify-around text-xs">
          {"✦✧✦✧✦✧✦✧✦✧✦✧✦✧".split("").map((s, i) => (
            <span key={i} className="text-white animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>{s}</span>
          ))}
        </div>
        <div className="relative max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <div className="text-2xl animate-bounce">🌌</div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-white text-sm md:text-base leading-tight tracking-wide truncate"
                 style={{ fontFamily: "Anton, sans-serif" }}>
              The Constellation Merchant has arrived!
            </div>
            <div className="text-[10px] md:text-xs text-amber-100/90 truncate">
              Closes in <span data-testid="constellation-banner-countdown"
                              className="font-black tabular-nums text-white">{mm}:{ss}</span> · Star-stitched cloak. Pockets full of fate.
            </div>
          </div>
          <Link
            to="/constellation"
            data-testid="constellation-banner-cta"
            className="px-4 py-1.5 rounded bg-black/30 hover:bg-black/50 border border-amber-200/60 text-white text-xs font-black uppercase tracking-wider transition whitespace-nowrap"
          >
            Visit Shop →
          </Link>
        </div>
      </div>
    </div>
  );
}
